import {AuthState} from '@/types/state'
import * as actionTypes from '@/store/action-types'
import * as mutationTypes from '@/store/mutation-types'

import {
  ChangePasswordPayload,
  ChangePasswordWithRecoveryCodePayload,
  PasswordRecoveryRequestPayload,
  UserCredentialsPayload
} from '@/types/payload'
import {CurrentUser} from '@/model/CurrentUser'
import * as getterTypes from '@/store/getter-types'
import axios from '@/plugins/axios'
import UserForPasswordRecoveryNotFound from '@/model/error/UserForPasswordRecoveryNotFound'
import {AxiosError, AxiosResponse} from "axios";
import {ApiError} from "@/types/service-response";
import UserDoesNotHaveAnyEmail from "@/model/error/UserDoesNotHaveAnyEmail";
import InvalidCredentialsError from "@/model/error/InvalidCredentialsError";
import {TOKEN_FIELD} from "@/types/constants";
import {replace} from "@/services/settings";
import Allergen from "@/model/Allergen";

const state: AuthState = {
  currentUser: undefined,
  token: undefined,
  isInvalidToken: false,
  isOrderConfirmation: false,
  isDeleteConfirmation: false
}

const getters = {
  [getterTypes.USER_LOGGED_IN](state: AuthState): CurrentUser | undefined {
    return state.currentUser
  },
  [getterTypes.AUTH_TOKEN] (state: AuthState): string | undefined {
    return state.token
  },
  [getterTypes.IS_INVALID_TOKEN] (state: AuthState): boolean {
    return state.isInvalidToken
  },
  [getterTypes.IS_ORDER_CONFIRMATION] (state: AuthState): boolean {
    return state.isOrderConfirmation
  },
  [getterTypes.IS_DELETE_CONFIRMATION] (state: AuthState): boolean {
    return state.isDeleteConfirmation
  }
}

const mutations = {
  [mutationTypes.AUTH_SET_CURRENT_USER_MUTATION](state: AuthState, payload: CurrentUser): void {
    state.currentUser = payload
  },
  [mutationTypes.AUTH_SET_TOKEN] (state: AuthState, payload: string): void {
    state.token = payload
    localStorage.setItem(TOKEN_FIELD, payload)
  },
  [mutationTypes.SET_INVALID_TOKEN_MUTATION] (state: AuthState, payload: boolean): void {
    state.isInvalidToken = payload
  },

  [mutationTypes.SET_IS_ORDER_CONFIRMATION_MUTATION](state: AuthState, payload: boolean): void {
    state.isOrderConfirmation = payload
  },

  [mutationTypes.SET_IS_DELETE_CONFIRMATION_MUTATION](state: AuthState, payload: boolean): void {
    state.isDeleteConfirmation = payload
  },

}

const actions = {
  [actionTypes.SET_INVALID_TOKEN]({commit}: { commit: Function }, payload: boolean): void {
    commit(mutationTypes.SET_INVALID_TOKEN_MUTATION, payload)
  },
  async [actionTypes.AUTH_LOG_USER_IN]({commit}: { commit: Function }, payload: UserCredentialsPayload): Promise<CurrentUser> {

    let data = {
      "username": payload.username,
      "password": payload.password
    }
    try {
      let res = await axios.post('/api/login', data)

      const token = res.data.token;

    let headers = {
      'Authorization': 'Bearer ' + token
    }

    let response: any = await axios.get('/api/account', {headers: headers})
    replace(response.data.settings)
    const user = CurrentUser.createFromUserInformationResponse({
      id: response.data.id,
      debtor: response.data.boarderList[0].debtor,
      givenName: response.data.firstName,
      familyName: response.data.familyName,
      isBoarder: response.data.isBoarder,
      qrCode: response.data.qrCode,
      isStaff: response.data.isStaff,
      staffRoleList: response.data.staffRoleList,
      boarderList: response.data.boarderList,
      loginName: response.data.loginName,
      tenant: response.data.tenant,
      phoneNumbers: response.data.phoneNumbers,
      currentCreditBalance: response.data.currentCreditBalance
    })

    localStorage.setItem('token', token);

    commit(mutationTypes.AUTH_SET_CURRENT_USER_MUTATION, user)
      return user
    } catch (e) {
      throw new InvalidCredentialsError()
    }
  },

  async [actionTypes.DELETE_CONFIRMATION]({commit}: { commit: Function }, payload: UserCredentialsPayload): Promise<boolean> {

    try {
      let res = await axios.post('/api/login', payload)

      const token = res.data.token;

      let headers = {
        'Authorization': 'Bearer ' + token
      }

      let response: any = await axios.get('/api/account', {headers: headers})

      if(state.currentUser && response.data && response.data && response.data.id === state.currentUser.id) {
        return true
      } else {
        return false
      }
    } catch (e) {
      return false
    }
  },

  async [actionTypes.ORDER_CONFIRMATION]({commit}: { commit: Function }, payload: UserCredentialsPayload): Promise<boolean> {

    let data = {
      "username": payload.username,
      "password": payload.password
    }
    try {
      let res = await axios.post('/api/login', data)

      const token = res.data.token;

      let headers = {
        'Authorization': 'Bearer ' + token
      }

      let response: any = await axios.get('/api/account', {headers: headers})

      if(state.currentUser && response.data && response.data && response.data.id === state.currentUser.id) {
        return true
      } else {
        return false
      }
    } catch (e) {
      return false
    }
  },

  async [actionTypes.SET_IS_ORDER_CONFIRMATION]({commit}: { commit: Function }, payload: boolean): Promise<void> {

    commit(mutationTypes.SET_IS_ORDER_CONFIRMATION_MUTATION, payload)
  },

  async [actionTypes.SET_IS_DELETE_CONFIRMATION]({commit}: { commit: Function }, payload: boolean): Promise<void> {

    commit(mutationTypes.SET_IS_DELETE_CONFIRMATION_MUTATION, payload)
  },


  async [actionTypes.AUTH_LOG_USER_OUT]({commit}: { commit: Function }): Promise<void> {

    commit(mutationTypes.AUTH_SET_CURRENT_USER_MUTATION, undefined)
    localStorage.setItem('token', '');

  },
  async [actionTypes.AUTH_LOG_USER_IN_WITH_TOKEN] ({ commit }: { commit: Function }, payload: string): Promise<CurrentUser> {

    let headers = {
      'Authorization': 'Bearer ' + payload
    }
    try {
      let res_user_account: AxiosResponse = await axios.get('/api/account', { headers:headers})
      replace(res_user_account.data.settings)

      const user = CurrentUser.createFromUserInformationResponse({
        id: res_user_account.data.id,
        debtor: res_user_account.data.boarderList[0].debtor,
        givenName: res_user_account.data.firstName,
        familyName: res_user_account.data.familyName,
        isBoarder: res_user_account.data.isBoarder,
        qrCode: res_user_account.data.qrCode,
        staffRoleList:res_user_account.data.staffRoleList,
        boarderList: res_user_account.data.boarderList,
        loginName: res_user_account.data.loginName,
        isStaff: res_user_account.data.isStaff,
        tenant: res_user_account.data.tenant,
        phoneNumbers: res_user_account.data.phoneNumbers,
        currentCreditBalance: res_user_account.data.currentCreditBalance
      })

      commit(mutationTypes.AUTH_SET_CURRENT_USER_MUTATION, user)

      return user
    } catch (e) {
      throw new InvalidCredentialsError()
    }
  },
  async [actionTypes.AUTH_INIT_MODULE] ({ commit }: { commit: Function }): Promise<void> {
    const authToken = localStorage.getItem(TOKEN_FIELD)

    if (authToken) {
      commit(mutationTypes.AUTH_SET_TOKEN, authToken)
    }
  },

  async [actionTypes.AUTH_CHANGE_PIN]({commit}: { commit: Function }, payload: ChangePasswordPayload): Promise<void> {
    const headers = {
      'Authorization': 'Bearer ' + localStorage.getItem('token')
    }

    const response = await axios.put('/api/account/password', payload, {headers: headers})
  },

  async [actionTypes.AUTH_REQUEST_PASSWORD_RECOVERY]({}, payload: PasswordRecoveryRequestPayload): Promise<void> {
    try {
      await axios.post('/api/password-recovery', payload)
    } catch (e) {
      const error: AxiosError = e as AxiosError

      if(error.response?.status === 400) {
        const match = (error.response?.data as ApiError).detail.match(/\[(\d+)\]/)

        if (match && match[1]) {
          const code = parseInt(match[1])

         switch (code) {
           case 10003:
             throw new UserDoesNotHaveAnyEmail

           default:
             throw new UserForPasswordRecoveryNotFound
         }
        }
      } else {
        throw e
      }
    }
  },

  async [actionTypes.AUTH_VALIDATE_RECOVERY_CODE]({}, payload: string): Promise<void> {
    await axios.get(`/api/password-recovery/${payload}`)
  },

  async [actionTypes.FETCH_ALLERGEN_LIST] (): Promise<Allergen[] | void> {
    try {
      const response = await axios.get('/api/food-database/allergen')

      return response.data
    } catch (e) {
    }
  },

  async [actionTypes.FETCH_FOOD_ITEM_IMAGES] ({},payload: any ): Promise<any> {

    const response = await axios.get(`/api/food-database/food-item-media/${payload.id}/data`)
    console.log(response.data)
    return response.data
  },


  async [actionTypes.AUTH_CHANGE_PASSWORD_WITH_RECOVERY_CODE]({}, payload: ChangePasswordWithRecoveryCodePayload): Promise<void> {
    await axios.put(`/api/password-recovery/${payload.recoveryCode}`, {
      password: payload.password
    })
  }
}

const authModule = {
  state,
  getters,
  mutations,
  actions
}

export default authModule
